import request from '@/utils/request'

// ============ Beta Groups ============

export interface BetaGroup {
  id: number
  groupKey: string
  groupName: string
  description: string | null
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Install state of one cane against the build its beta group targets.
// 'unknown' means the cane has never reported its firmware (older mobile
// builds do not send it), NOT that the update failed.
export type BetaMemberDeviceStatus = 'installed' | 'outdated' | 'unknown'

export interface BetaGroupMemberDevice {
  serialNumber: string
  productType: string | null
  installedAppVersion: string | null
  installedWirelessVersion: string | null
  installedReportedAt: string | null
  targetAppVersion: string | null
  targetWirelessVersion: string | null
  status: BetaMemberDeviceStatus
}

export interface BetaGroupMember {
  id: number
  userId: string
  groupId: number
  assignedBy: string | null
  assignedAt: string
  betaGroup?: BetaGroup
  user?: {
    userId: string
    email: string | null
    firstName: string | null
    lastName: string | null
  } | null
  devices?: BetaGroupMemberDevice[]
  lastServed?: {
    applicationVersionName: string | null
    wirelessVersionName: string | null
    servedAt: string
  } | null
}

export interface BetaGroupMemberSummary {
  totalMembers: number
  reportingMembers: number
  onTargetMembers: number
  targets: Array<{
    majorVersion: string | null
    applicationVersionName: string
    wirelessVersionName: string
  }>
}

export const getBetaGroups = () =>
  request({
    url: '/admin/beta-groups',
    method: 'get',
    timeout: 30000
  })

export const createBetaGroup = (data: {
  groupKey: string
  groupName: string
  description?: string
  isDefault?: boolean
  isActive?: boolean
}) =>
  request({
    url: '/admin/beta-groups',
    method: 'post',
    data
  })

export const updateBetaGroup = (id: number, data: {
  groupName?: string
  description?: string
  isDefault?: boolean
  isActive?: boolean
}) =>
  request({
    url: `/admin/beta-groups/${id}`,
    method: 'put',
    data
  })

export const deleteBetaGroup = (id: number) =>
  request({
    url: `/admin/beta-groups/${id}`,
    method: 'delete'
  })

// ============ Beta Group Members ============

export const getBetaGroupMembers = (groupId: number) =>
  request({
    url: `/admin/beta-groups/${groupId}/members`,
    method: 'get'
  })

export const addUserToBetaGroup = (data: { userId: string; groupId: number }) =>
  request({
    url: '/admin/beta-groups/members',
    method: 'post',
    data
  })

export const addUsersToBetaGroup = (data: { userIds: string[]; groupId: number }) =>
  request({
    url: '/admin/beta-groups/members/bulk',
    method: 'post',
    data,
    timeout: 120000
  })

export const removeUsersFromBetaGroup = (data: { userIds: string[] }) =>
  request({
    url: '/admin/beta-groups/members/bulk-remove',
    method: 'post',
    data,
    timeout: 120000
  })

export const removeUserFromBetaGroup = (userId: string) =>
  request({
    url: `/admin/beta-groups/members/${userId}`,
    method: 'delete'
  })

// ============ OTA Versions for Group ============

export const getOtaVersionsForGroup = (groupId: number | string) =>
  request({
    url: `/admin/beta-groups/${groupId}/ota-versions`,
    method: 'get'
  })
